import { useState } from 'react';
import axios from 'axios';
import { AlertCircle, CheckCircle, Gift, RefreshCw, X, Clock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../utils/dateFormat';
import { formatPlanLabel, formatTierLabel } from '../../constants/subscriptionPlans';

import PageTitle from '../../components/PageTitle';

export default function GiftCard() {
  const { user, refreshUser } = useAuth();
  const subscription = user?.subscription;
  const { t } = useTranslation(['settings', 'common']);

  const [giftCardCode, setGiftCardCode] = useState('');
  const [verifyingCard, setVerifyingCard] = useState(false);
  const [redeemingCard, setRedeemingCard] = useState(false);
  const [giftCardResult, setGiftCardResult] = useState<{ status: 'success' | 'error'; message: string } | null>(null);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [verifiedGiftCard, setVerifiedGiftCard] = useState<{
    code: string;
    planType?: string;
    tier?: string;
    durationDays?: number;
    newExpiry: string;
  } | null>(null);

  const handleVerifyAndShowModal = async () => {
    if (!giftCardCode.trim()) {
      setGiftCardResult({ status: 'error', message: t('gift_card.enter_code') });
      return;
    }

    setGiftCardResult(null);
    setVerifyingCard(true);
    try {
      await refreshUser();
      const response = await axios.post('/api/gift-cards/verify', { code: giftCardCode.trim() });
      const { planType, tier, durationDays, newExpiresAt } = response.data;

      if (!durationDays) {
        setGiftCardResult({ status: 'error', message: t('gift_card.invalid_card') });
        setVerifyingCard(false);
        return;
      }

      const newExpiry = newExpiresAt
        ? new Date(newExpiresAt)
        : new Date(Date.now() + durationDays * 24 * 60 * 60 * 1000);

      setVerifiedGiftCard({
        code: giftCardCode.trim(),
        planType,
        tier,
        durationDays,
        newExpiry: formatDate(newExpiry),
      });
      setShowRedeemModal(true);
    } catch (error: any) {
      const message = error.response?.data?.error || t('gift_card.verify_failed');
      setGiftCardResult({ status: 'error', message });
    } finally {
      setVerifyingCard(false);
    }
  };

  const handleRedeemGiftCard = async () => {
    if (!verifiedGiftCard) return;

    setRedeemingCard(true);
    try {
      await axios.post('/api/gift-cards/redeem', { code: verifiedGiftCard.code });
      setGiftCardResult({
        status: 'success',
        message: t('gift_card.redeemed_success', { date: verifiedGiftCard.newExpiry }),
      });
      setShowRedeemModal(false);
      setVerifiedGiftCard(null);
      setGiftCardCode('');
      await refreshUser();
    } catch (error: any) {
      const message = error.response?.data?.error || t('gift_card.redeem_failed');
      setGiftCardResult({ status: 'error', message });
    } finally {
      setRedeemingCard(false);
    }
  };

  return (
    <div className="space-y-8">
      <PageTitle 
        titleKey="gift_card.title" 
        namespace="settings" 
        descriptionKey="gift_card.subtitle"
      />
      {/* Section Header */}
      <div>
        <h2 className="text-xl font-semibold text-foreground tracking-[-0.04em]">{t('gift_card.title')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('gift_card.subtitle')}
        </p>
      </div>

      {/* Current subscription status (brief) */}
      {subscription?.expiresAt && (
        <div className="bg-card rounded-lg border border-border p-4 shadow-xs">
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('gift_card.current_subscription')}</p>
              <p className="text-sm font-medium text-foreground mt-0.5">
                {formatTierLabel(subscription.tier)} — {t('gift_card.expires')}{' '}
                {formatDate(subscription.expiresAt)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Redeem Gift Card */}
      <div className="bg-card rounded-lg border border-border p-5 shadow-xs">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Gift className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">{t('gift_card.redeem_title')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('gift_card.redeem_subtitle')}
            </p>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-start">
          <input
            type="text"
            value={giftCardCode}
            onChange={(e) => setGiftCardCode(e.target.value.toUpperCase())}
            placeholder={t('gift_card.code_placeholder')}
            className="w-full px-4 py-3 border border-border rounded-xl bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
            disabled={verifyingCard || redeemingCard}
            onKeyDown={(e) => e.key === 'Enter' && handleVerifyAndShowModal()}
          />
          <button
            type="button"
            onClick={handleVerifyAndShowModal}
            disabled={verifyingCard || redeemingCard || !giftCardCode.trim()}
            className="px-8 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
          >
            {verifyingCard ? (
              <>
                <RefreshCw className="inline-block h-4 w-4 mr-2 animate-spin" />
                {t('gift_card.verifying')}
              </>
            ) : (
              t('common:confirm')
            )}
          </button>
        </div>

        {giftCardResult && (
          <div
            className={`mt-3 flex items-center gap-2 rounded-xl border px-4 py-3 text-sm ${
              giftCardResult.status === 'success'
                ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                : 'border-destructive/20 bg-destructive/10 text-destructive'
            }`}
          >
            {giftCardResult.status === 'success' ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            <span>{giftCardResult.message}</span>
          </div>
        )}
      </div>

      {/* How It Works */}
      <div className="bg-card rounded-lg border border-border p-5 shadow-xs">
        <h3 className="text-base font-semibold text-foreground mb-3">{t('gift_card.how_it_works')}</h3>
        <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
          <li>{t('gift_card.step_1')}</li>
          <li>{t('gift_card.step_2')}</li>
          <li>{t('gift_card.step_3')}</li>
          <li>{t('gift_card.step_4')}</li>
        </ol>
      </div>

      {/* Redeem Confirmation Modal */}
      {showRedeemModal && verifiedGiftCard && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          aria-labelledby="redeem-modal-title"
          role="dialog"
          aria-modal="true"
          onKeyDown={(e) => e.key === 'Escape' && !redeemingCard && setShowRedeemModal(false)}
          tabIndex={-1}
        >
          <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              aria-hidden="true"
            ></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            {/* Modal Panel */}
            <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-lg transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full border border-border">
              {/* Header */}
              <div className="bg-primary/5 px-6 pt-6 pb-4 border-b border-border">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 flex items-center justify-center h-14 w-14 rounded-lg bg-primary/10 shadow-xs">
                    <Gift className="h-7 w-7 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-foreground" id="redeem-modal-title">
                      {t('gift_card.confirm_title')}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">{t('gift_card.confirm_subtitle')}</p>
                  </div>
                  {!redeemingCard && (
                    <button
                      type="button"
                      onClick={() => setShowRedeemModal(false)}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-6 space-y-4">
                {/* Gift Card Details */}
                <div className="bg-muted/30 rounded-xl p-4 space-y-3 border border-border">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">{t('gift_card.gift_card_code')}</span>
                    <code className="bg-muted px-3 py-1 rounded-lg text-sm font-mono text-foreground">
                      {verifiedGiftCard.code}
                    </code>
                  </div>
                  {verifiedGiftCard.tier && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-muted-foreground">{t('gift_card.tier_label')}</span>
                      <span className="text-base font-bold text-foreground capitalize">
                        {formatTierLabel(verifiedGiftCard.tier)}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">{t('gift_card.pass_type')}</span>
                    <span className="text-base font-bold text-foreground">
                      {formatPlanLabel(verifiedGiftCard.planType) || t('common:custom')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">{t('gift_card.duration')}</span>
                    <span className="text-base font-semibold text-foreground">
                      {t('gift_card.duration_days', { count: verifiedGiftCard.durationDays })}
                    </span>
                  </div>
                </div>

                {/* New Expiry Info */}
                <div className="bg-emerald-500/10 border-2 border-emerald-500/20 rounded-xl p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-xl bg-emerald-500/20">
                      <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300 mb-1">
                        {t('gift_card.subscription_will_expire')}
                      </p>
                      <p className="text-lg font-bold text-emerald-800 dark:text-emerald-200">
                        {verifiedGiftCard.newExpiry}
                      </p>
                      {subscription?.expiresAt && (
                        <p className="text-xs text-emerald-600/80 dark:text-emerald-400/80 mt-1">
                          {(() => {
                            const currentExpiry = new Date(subscription.expiresAt);
                            const now = new Date();
                            if (currentExpiry > now) {
                              return t('gift_card.current_expiry', { date: formatDate(currentExpiry) });
                            }
                            return t('gift_card.no_active_sub');
                          })()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Info Message */}
                <div className="bg-blue-500/10 border-l-4 border-blue-500 p-4 rounded-r">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-blue-500" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        {t('gift_card.confirm_info')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="bg-muted/30 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 border-t border-border">
                <button
                  type="button"
                  onClick={() => setShowRedeemModal(false)}
                  disabled={redeemingCard}
                  className="w-full sm:w-auto inline-flex justify-center items-center px-6 py-2.5 border border-border shadow-sm text-sm font-semibold rounded-xl text-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {t('common:cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleRedeemGiftCard}
                  disabled={redeemingCard}
                  className="w-full sm:w-auto inline-flex justify-center items-center gap-2 px-8 py-2.5 border border-transparent shadow-sm text-sm font-semibold rounded-xl text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {redeemingCard ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      {t('gift_card.redeeming')}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      {t('gift_card.confirm_redemption')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
